// Контракт печати этикетки. ТОЛЬКО интерфейс + заглушка превью.
//
// Реализация печати — задача T-007 (нативный Bluetooth-плагин Capacitor, TSPL).
// Здесь каркас фиксирует форму контракта, потому что менять её задним числом дорого:
// на неё завязаны экран печати (T-002) и офлайн-очередь (T-007).
//
// ТРЁХСЛОЙНЫЙ КОНТРАКТ (offline-first.md §8). Слои не смешивать:
//
//   1. Рендер     — этикетка → 1-битный битмап 8 точек/мм. Слой один и несменный.
//                   Битмап решает кириллицу (у китайских TSPL-принтеров нет русских
//                   шрифтов) и печатает вёрстку из DESIGN.md как есть.
//   2. Язык команд — битмап → байты. Сменный: TSPL/TSPL2, растровые проприетарные.
//   3. Транспорт   — байты → принтер. Сменный: BT Classic SPP, BLE GATT, USB, TCP 9100.
//
// Смена модели принтера обязана быть записью в Device (модель, транспорт, язык, DPI),
// а не правкой кода. Вёрстка этикетки не знает про язык команд, язык команд не знает
// про транспорт.
//
// ЗАПРЕТ: браузерный Bluetooth-API в транспорте не используется — он не умеет
// Bluetooth Classic SPP, требует системный диалог при каждом переподключении и
// печатает этикетку за 20+ секунд вместо 0,5. Транспорт — только нативный плагин
// Capacitor (app.md §1, offline-first.md §8); запрет продублирован в ESLint.

/**
 * Данные этикетки для рендера.
 *
 * Осознанно нетипизированы: доменная модель PrintJob появляется в T-002/T-003,
 * и каркас не должен её предугадывать. Форма контракта зафиксирована, поля — нет.
 */
export type LabelSource = Readonly<Record<string, unknown>>;

/** Слой 1: результат рендера — упакованный 1-битный растр. */
export type LabelBitmap = {
  readonly widthPx: number;
  readonly heightPx: number;
  /** Плотность печати. Для целевых портативных TSPL — 8 точек/мм (203 dpi). */
  readonly dotsPerMm: number;
  /** Биты растра, 1 — чёрная точка. Длина = ceil(widthPx / 8) * heightPx. */
  readonly bits: Uint8Array;
};

/** Слой 1: рендер этикетки в битмап. */
export interface LabelRenderer {
  render(source: LabelSource): Promise<LabelBitmap>;
}

/** Языки команд принтера. Значения уезжают в Device как строки. */
export const COMMAND_LANGUAGES = {
  tspl: 'tspl',
  raster: 'raster',
  preview: 'preview',
} as const;

export type CommandLanguageId = (typeof COMMAND_LANGUAGES)[keyof typeof COMMAND_LANGUAGES];

/** Слой 2: битмап → байты команд конкретного языка. */
export interface CommandLanguage {
  readonly id: CommandLanguageId;
  encode(bitmap: LabelBitmap): Uint8Array;
}

/** Транспорты доставки байтов до принтера. Значения уезжают в Device как строки. */
export const PRINTER_TRANSPORTS = {
  btClassicSpp: 'bt-classic-spp',
  bleGatt: 'ble-gatt',
  usb: 'usb',
  tcp9100: 'tcp-9100',
  preview: 'preview',
} as const;

export type PrinterTransportId = (typeof PRINTER_TRANSPORTS)[keyof typeof PRINTER_TRANSPORTS];

/** Слой 3: байты → принтер. */
export interface PrinterTransport {
  readonly id: PrinterTransportId;
  send(bytes: Uint8Array): Promise<void>;
  /**
   * Разорвать соединение, если оно держится между вызовами `send` (Д7/Д9 на живом
   * железе, 2026-09-05).
   *
   * Bluetooth Classic SPP на V3BT держит РОВНО ОДНО соединение разом (спайк T-035,
   * вопрос 6): пока один транспорт (BT-телефон или COM-порт планшета) не закрыл своё,
   * второе устройство подключиться не может вообще — не «печать не удалась», а «нет
   * связи с принтером» на пустом месте, хотя принтер исправен и включён.
   *
   * Необязателен: у транспорта без держащегося соединения (`winspool` — печать через
   * очередь Windows заново на каждое задание) закрывать нечего.
   */
  disconnect?(): Promise<void>;
}

/**
 * Результат печати.
 *
 * Discriminated union, а не void: v1 ничего физически не печатает, и вызывающий код
 * обязан различать «байты ушли в принтер» и «показываем превью на экране».
 */
export type PrintOutcome =
  | { readonly kind: 'printed'; readonly transport: PrinterTransportId; readonly bytesSent: number }
  | { readonly kind: 'preview'; readonly source: LabelSource };

/**
 * Причина отказа печати — по ней экран печати выбирает сообщение и подсказку.
 * `busy` отделён намеренно: один принтер на кухню, устройств несколько (D6), и
 * «занят другим устройством» — это «подожди и повтори», а не «принтер сломан».
 */
export type PrinterErrorKind = 'busy' | 'unknown';

/**
 * Ошибка печати: нет соединения с принтером, отказ в доступе, нет бумаги.
 * Печать не имеет права «молча ничего не сделать» (offline-first.md §8).
 */
export class PrinterError extends Error {
  readonly kind: PrinterErrorKind;

  constructor(
    message: string,
    readonly originalError?: unknown,
    kind: PrinterErrorKind = 'unknown',
  ) {
    super(message, { cause: originalError });
    this.name = 'PrinterError';
    this.kind = kind;
  }
}

/** Адаптер печати: собранная связка рендер → язык команд → транспорт. */
export interface PrinterAdapter {
  readonly language: CommandLanguageId;
  readonly transport: PrinterTransportId;
  print(source: LabelSource): Promise<PrintOutcome>;
  /** Пробрасывает `PrinterTransport.disconnect`, если у транспорта есть что закрывать. */
  disconnect?(): Promise<void>;
}

/**
 * Единственная реализация в v1: превью вместо физической печати.
 *
 * Физической печати в v1 нет — пилот проверяет логику (каталог, срок, статусы,
 * офлайн), а не железо. Адаптер стоит на том же контракте, поэтому включение
 * реального принтера — это привязка в Device / фиче-флаг, а не переписывание потока
 * печати (offline-first.md §8).
 *
 * Рендер в битмап здесь не выполняется намеренно: превью рисует компонент
 * LabelPreview из @restomarker/ui-kit по тем же данным, а растр нужен только
 * аппаратным адаптерам.
 */
export const previewAdapter: PrinterAdapter = {
  language: COMMAND_LANGUAGES.preview,
  transport: PRINTER_TRANSPORTS.preview,

  print(source: LabelSource): Promise<PrintOutcome> {
    return Promise.resolve({ kind: 'preview', source });
  },
};
